import React, { useState, useEffect } from 'react';
import { Button, Space, Tag, Drawer, Form, Input, InputNumber, Select, Modal, Descriptions, Table, Radio, Checkbox, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getPlatformList, savePlatform, getModelList, saveModel, updateModelStatus } from '../../api/config';
import { hasPerm } from '../../hooks/usePermission';

const PlatformConfig = () => {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const [modelDrawerOpen, setModelDrawerOpen] = useState(false);
    const [currentPlatform, setCurrentPlatform] = useState(null);
    const [models, setModels] = useState([]);
    const [modelLoading, setModelLoading] = useState(false);

    const [modelModalOpen, setModelModalOpen] = useState(false);
    const [modelEditing, setModelEditing] = useState(null);
    const [modelForm] = Form.useForm();

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getPlatformList();
            if (res.code === 0) {
                setDataSource(res.data.list);
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch platform list failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const openEdit = (record) => {
        setEditing(record);
        setDrawerOpen(true);
        form.setFieldsValue({
            name: record.name,
            code: record.code,
            status: record.status,
            fields: record.fields,
        });
    };

    const openAdd = () => {
        setEditing(null);
        setDrawerOpen(true);
        form.resetFields();
    };

    const handleSetKey = (index) => {
        const fields = form.getFieldValue('fields') || [];
        fields.forEach((_, i) => {
            if (i !== index) form.setFieldValue(['fields', i, 'is_key'], false);
        });
    };

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            if (error.errorFields) return;
            throw error;
        }
        const res = await savePlatform(values);
        if (res.code === 0) {
            message.success('保存成功');
            setDrawerOpen(false);
            fetchList();
        } else {
            message.error(res.message || '保存失败');
        }
    };

    const fetchModels = async (platform) => {
        setModelLoading(true);
        try {
            const res = await getModelList({ platform });
            if (res.code === 0) {
                setModels(res.data.list);
            }
        } catch (error) {
            console.error('Fetch model list failed:', error);
        } finally {
            setModelLoading(false);
        }
    };

    const openModelDrawer = (record) => {
        setCurrentPlatform(record);
        setModelDrawerOpen(true);
        fetchModels(record.name);
    };

    const openAddModel = () => {
        setModelEditing(null);
        modelForm.resetFields();
        setModelModalOpen(true);
    };

    const openEditModel = (model) => {
        setModelEditing(model);
        modelForm.setFieldsValue({ name: model.name, type: model.type, mac_step: model.mac_step ?? 1 });
        setModelModalOpen(true);
    };

    const handleSaveModel = async () => {
        let values;
        try {
            values = await modelForm.validateFields();
        } catch (error) {
            if (error.errorFields) return;
            throw error;
        }
        const res = await saveModel({ platform: currentPlatform.name, id: modelEditing?.id, ...values });
        if (res.code === 0) {
            message.success('保存成功');
            setModelModalOpen(false);
            fetchModels(currentPlatform.name);
            fetchList();
        } else {
            message.error(res.message || '保存失败');
        }
    };

    const handleToggleStatus = async (model) => {
        const newStatus = model.status === '启用' ? '停用' : '启用';
        const res = await updateModelStatus({ platform: currentPlatform.name, id: model.id, status: newStatus });
        if (res.code === 0) {
            message.success(newStatus === '启用' ? '已启用' : '已停用');
            fetchModels(currentPlatform.name);
            fetchList();
        } else {
            message.error(res.message || '操作失败');
        }
    };

    const modelColumns = [
        { title: '型号名称', dataIndex: 'name', key: 'name' },
        { title: '设备类型', dataIndex: 'type', key: 'type', width: 140 },
        {
            title: '生成MAC步长', dataIndex: 'mac_step', key: 'mac_step', width: 120,
            render: (v) => v ?? 1,
        },
        {
            title: '状态', dataIndex: 'status', key: 'status', width: 100,
            render: (v) => <Tag color={v === '启用' ? 'success' : 'default'}>{v}</Tag>,
        },
        {
            title: '操作', key: 'action', width: 160,
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, model) => (
                <Space>
                    {hasPerm('config:model:edit') && (
                        <Button type="link" onClick={() => openEditModel(model)}>编辑</Button>
                    )}
                    {hasPerm('config:model:toggle') && (
                        <Button type="link" onClick={() => handleToggleStatus(model)}>
                            {model.status === '启用' ? '停用' : '启用'}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const columns = [
        { title: '平台名称', dataIndex: 'name', key: 'name', width: 120 },
        { title: '平台编码', dataIndex: 'code', key: 'code', width: 120 },
        {
            title: '字段规则', dataIndex: 'fields', key: 'fields',
            render: (fields) => (
                <Space wrap>
                    {(fields || []).map((f) => (
                        <Tag key={f.name} color="blue">{f.name}（{(f.lengths || []).join('位、')}位）</Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: '密钥', dataIndex: 'fields', key: 'key_field', width: 100,
            render: (fields) => {
                const keyField = (fields || []).find((f) => f.is_key);
                return keyField ? <Tag color="gold">{keyField.name}</Tag> : '-';
            },
        },
        {
            title: '状态', dataIndex: 'status', key: 'status', width: 90,
            render: (v) => <Tag color={v === '启用' ? 'success' : 'default'}>{v}</Tag>,
        },
        {
            title: '设备型号(启用)', dataIndex: 'model_enabled_count', key: 'model_enabled_count', width: 140,
            render: (count, record) => (
                <Button type="link" onClick={() => openModelDrawer(record)}>{count}</Button>
            ),
        },
        {
            title: '操作', key: 'action', width: 100,
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                hasPerm('config:platform:edit') && (
                    <Button type="link" onClick={() => openEdit(record)}>编辑</Button>
                )
            ),
        },
    ];

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
                {hasPerm('config:platform:add') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增平台</Button>
                )}
            </div>

            <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} pagination={false} />

            <Drawer
                title={editing ? '编辑运营平台' : '新增运营平台'}
                width={520}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
                            <Button type="primary" onClick={handleSave}>保存</Button>
                        </Space>
                    </div>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="平台名称" rules={[{ required: true, message: '请输入平台名称' }]}>
                        <Input placeholder="如：电信 / 移动 / 联通 / 涂鸦" />
                    </Form.Item>
                    <Form.Item name="code" label="平台编码" rules={[{ required: true, message: '请输入平台编码' }]}>
                        <Input placeholder="如：telecom" />
                    </Form.Item>
                    <Form.Item name="status" label="状态" initialValue="启用">
                        <Radio.Group>
                            <Radio value="启用">启用</Radio>
                            <Radio value="停用">停用</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="字段规则（字段名 + 支持多长度 + 密钥）">
                        <Form.List name="fields">
                            {(fields, { add: addField, remove: removeField }) => (
                                <div>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <div key={key} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                                            <Space align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                                <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true, message: '字段名' }]} style={{ marginBottom: 0 }}>
                                                    <Input placeholder="字段名（如 CTEI）" style={{ width: 140 }} />
                                                </Form.Item>
                                                <Form.Item name={[name, 'is_key']} valuePropName="checked" noStyle>
                                                    <Checkbox onChange={(e) => { if (e.target.checked) handleSetKey(name); }}>设为密钥</Checkbox>
                                                </Form.Item>
                                                <Button type="link" danger onClick={() => removeField(name)}>删除字段</Button>
                                            </Space>
                                            <div style={{ marginBottom: 8, color: 'rgba(0, 0, 0, 0.85)' }}>长度：</div>
                                            <Form.List name={[name, 'lengths']}>
                                                {(lenFields, { add: addLen, remove: removeLen }) => (
                                                    <div>
                                                        <Space wrap>
                                                            {lenFields.map((lenField) => (
                                                                <Space key={lenField.key} align="baseline">
                                                                    <Form.Item
                                                                        name={lenField.name}
                                                                        rules={[
                                                                            { required: true, message: '长度' },
                                                                            {
                                                                                validator: (_, value) => {
                                                                                    if (value === null || value === undefined) return Promise.resolve();
                                                                                    const lengths = form.getFieldValue(['fields', name, 'lengths']) || [];
                                                                                    if (lengths.filter((l) => l === value).length > 1) {
                                                                                        return Promise.reject(new Error('长度不可重复'));
                                                                                    }
                                                                                    return Promise.resolve();
                                                                                },
                                                                            },
                                                                        ]}
                                                                        style={{ marginBottom: 0 }}
                                                                    >
                                                                        <InputNumber min={1} style={{ width: 120 }} />
                                                                    </Form.Item>
                                                                    <Button type="link" danger size="small" onClick={() => removeLen(lenField.name)}>删除</Button>
                                                                </Space>
                                                            ))}
                                                        </Space>
                                                        <Button type="dashed" size="small" onClick={() => addLen(16)} style={{ marginTop: 4 }}>+ 添加长度</Button>
                                                    </div>
                                                )}
                                            </Form.List>
                                        </div>
                                    ))}
                                    <Button type="dashed" block onClick={() => addField({ name: '', lengths: [16], is_key: false })}>+ 添加字段</Button>
                                </div>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Drawer>

            <Drawer
                title="设备型号管理"
                width={640}
                open={modelDrawerOpen}
                onClose={() => setModelDrawerOpen(false)}
            >
                {currentPlatform && (
                    <>
                        <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="平台名称">{currentPlatform.name}</Descriptions.Item>
                            <Descriptions.Item label="平台编码">{currentPlatform.code}</Descriptions.Item>
                            <Descriptions.Item label="字段规则">
                                <Space wrap>
                                    {(currentPlatform.fields || []).map((f) => (
                                        <Tag key={f.name} color="blue">{f.name}（{(f.lengths || []).join('位、')}位）</Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600 }}>设备型号列表</span>
                            {hasPerm('config:model:add') && (
                                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModel}>添加设备型号</Button>
                            )}
                        </div>

                        <Table rowKey="id" columns={modelColumns} dataSource={models} loading={modelLoading} pagination={false} size="small" />
                    </>
                )}
            </Drawer>

            <Modal
                title={modelEditing ? '编辑设备型号' : '添加设备型号'}
                open={modelModalOpen}
                onOk={handleSaveModel}
                onCancel={() => setModelModalOpen(false)}
                okText="保存"
                cancelText="取消"
                centered
            >
                <Form form={modelForm} layout="vertical">
                    <Form.Item name="name" label="型号名称" rules={[{ required: true, message: '请输入型号名称' }]}>
                        <Input placeholder="如 YLF-X1" />
                    </Form.Item>
                    <Form.Item name="type" label="设备类型" rules={[{ required: true, message: '请选择设备类型' }]}>
                        <Select placeholder="请选择" options={['网关', '模组', '传感器', '其他'].map((v) => ({ value: v, label: v }))} />
                    </Form.Item>
                    <Form.Item name="mac_step" label="生成MAC步长" initialValue={1} rules={[{ required: true, message: '请输入生成MAC步长' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="一条串码生成几个 MAC，默认 1" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PlatformConfig;
